import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TREND_LOCATIONS, DEFAULT_LOCATION } from '../data/trendLocations';
import { trendsAPI } from '../services/apiEndpoints';
import QuickPost from './QuickPost';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import {
    MagnifyingGlassIcon,
    Cross2Icon,
    PlusIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    MinusIcon,
    CheckCircledIcon,
    RocketIcon,
    TargetIcon,
    ReloadIcon
} from '@radix-ui/react-icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const KEYWORD_COLORS = [
    { line: '#8b5cf6', badge: 'bg-violet-500/20 text-violet-300 border-violet-500/30' },
    { line: '#3b82f6', badge: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
    { line: '#10b981', badge: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
    { line: '#f59e0b', badge: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
    { line: '#ef4444', badge: 'bg-red-500/20 text-red-300 border-red-500/30' },
];

function KeywordComparison({ user }) {
    const navigate = useNavigate();
    const [keywords, setKeywords] = useState(['', '']);
    const [locationCode, setLocationCode] = useState(DEFAULT_LOCATION);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [comparison, setComparison] = useState(null);

    const [cachedAt, setCachedAt] = useState(null);
    const [isFresh, setIsFresh] = useState(false);
    const [recentSearches, setRecentSearches] = useState([]);

    const [showQuickPost, setShowQuickPost] = useState(false);
    const [prefilledTopic, setPrefilledTopic] = useState('');

    useEffect(() => { loadRecentSearches(); }, []);

    const loadRecentSearches = async () => {
        try {
            const response = await trendsAPI.getCompareRecentSearches();
            if (response.data.success) setRecentSearches(response.data.data);
        } catch (err) { /* non-critical */ }
    };

    const handleAddKeyword = () => {
        if (keywords.length < 5) setKeywords([...keywords, '']);
    };

    const handleRemoveKeyword = (index) => {
        if (keywords.length > 2) setKeywords(keywords.filter((_, i) => i !== index));
    };

    const handleKeywordChange = (index, value) => {
        const updated = [...keywords];
        updated[index] = value;
        setKeywords(updated);
    };

    const handleCompare = async (forceRefresh = false, overrideKeywords = null, overrideLocation = null) => {
        const filled = (overrideKeywords ?? keywords).map(k => k.trim()).filter(Boolean);
        const loc = overrideLocation ?? locationCode;
        if (filled.length < 2) {
            setError('Please enter at least 2 keywords to compare');
            return;
        }
        setLoading(true);
        setError(null);
        if (!forceRefresh) setComparison(null);
        try {
            const response = await trendsAPI.compareKeywords(filled, loc, forceRefresh);
            if (response.data.success) {
                setComparison(response.data.data);
                setCachedAt(response.data.cached_at);
                setIsFresh(response.data.is_fresh ?? true);
                if (!forceRefresh) loadRecentSearches();
            } else {
                setError(response.data.message || 'Failed to compare keywords');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to compare keywords. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleCompare();
    };

    const getTrendIcon = (direction) => {
        if (direction === 'rising') return ArrowUpIcon;
        if (direction === 'declining') return ArrowDownIcon;
        return MinusIcon;
    };

    const getTrendColor = (direction) => {
        if (direction === 'rising') return 'text-emerald-400';
        if (direction === 'declining') return 'text-red-400';
        return 'text-zinc-400';
    };

    // Transform chart_data {labels, datasets} into recharts format [{label, kw1, kw2, ...}]
    const buildChartData = (chartData) => {
        if (!chartData?.labels?.length) return [];
        return chartData.labels.map((label, i) => {
            const point = { label };
            chartData.datasets.forEach(ds => {
                point[ds.label] = ds.data[i] ?? 0;
            });
            return point;
        });
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        return (
            <div className="bg-zinc-900 border border-white/10 rounded-lg p-3 shadow-xl text-xs space-y-1">
                <p className="text-zinc-400 mb-2">{label}</p>
                {payload.map((entry, i) => (
                    <div key={i} className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: entry.color }} />
                        <span className="text-zinc-300">{entry.name}:</span>
                        <span className="text-white font-medium">{entry.value}</span>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="w-full">
                <main className="p-6 overflow-y-auto custom-scrollbar">
                    <div className="max-w-5xl mx-auto space-y-6">

                        {/* Header */}
                        {!comparison && !loading && (
                            <div className="text-center space-y-3 max-w-xl mx-auto py-6">
                                <h1 className="scroll-m-20 text-3xl font-extrabold tracking-tight bg-gradient-to-r from-white via-zinc-200 to-zinc-400 bg-clip-text text-transparent">
                                    Keyword Comparison Tool
                                </h1>
                                <p className="leading-6 text-zinc-400 text-sm">
                                    Compare up to 5 keywords side-by-side to choose the winning topic
                                </p>
                            </div>
                        )}

                        {/* Input Card */}
                        <Card className="bg-zinc-900/40 backdrop-blur-xl border-white/10">
                            <CardHeader>
                                <CardTitle className="text-lg text-white">Enter Keywords to Compare</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-3">
                                    {keywords.map((keyword, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <span className="w-6 text-sm text-zinc-500 shrink-0">{index + 1}.</span>
                                            <input
                                                type="text"
                                                className="flex-1 bg-zinc-800/50 border border-zinc-700 rounded-lg px-4 py-2 text-sm text-white placeholder:text-zinc-500 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none"
                                                placeholder={`Enter keyword ${index + 1}...`}
                                                value={keyword}
                                                onChange={(e) => handleKeywordChange(index, e.target.value)}
                                                onKeyDown={handleKeyDown}
                                            />
                                            {keywords.length > 2 && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleRemoveKeyword(index)}
                                                    className="text-zinc-400 hover:text-red-400 shrink-0"
                                                >
                                                    <Cross2Icon className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {keywords.length < 5 && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleAddKeyword}
                                        className="w-full gap-2"
                                    >
                                        <PlusIcon className="w-4 h-4" />
                                        Add Another Keyword
                                    </Button>
                                )}

                                <div className="flex gap-3 pt-2">
                                    <Select value={locationCode} onValueChange={setLocationCode}>
                                        <SelectTrigger className="flex-1 bg-zinc-800/50 border-zinc-700">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {TREND_LOCATIONS.map(loc => (
                                                <SelectItem key={loc.code} value={loc.code}>
                                                    {loc.flag} {loc.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button
                                        onClick={() => handleCompare(false)}
                                        disabled={loading}
                                        className="bg-violet-600 hover:bg-violet-700 text-white gap-2"
                                    >
                                        {loading
                                            ? <><ReloadIcon className="w-4 h-4 animate-spin" /> Comparing...</>
                                            : <><MagnifyingGlassIcon className="w-4 h-4" /> Compare Keywords</>
                                        }
                                    </Button>
                                </div>

                                {error && (
                                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-300 text-sm">
                                        {error}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Recent searches */}
                        {recentSearches.length > 0 && (
                            <div className="flex flex-wrap gap-2 items-center">
                                <span className="text-xs text-zinc-500 font-medium">Recent:</span>
                                {recentSearches.map((search, i) => (
                                    <Badge
                                        key={i}
                                        onClick={() => {
                                            setKeywords(search.keywords);
                                            setLocationCode(search.location_code);
                                            handleCompare(false, search.keywords, search.location_code);
                                        }}
                                        className="cursor-pointer bg-zinc-800/50 hover:bg-zinc-700/50 text-zinc-300 hover:text-white border-white/5 hover:border-white/10 transition-all text-xs px-3 py-1"
                                    >
                                        {search.keywords.join(' vs ')}
                                    </Badge>
                                ))}
                            </div>
                        )}

                        {/* Loading */}
                        {loading && (
                            <div className="py-20 flex flex-col items-center justify-center space-y-6">
                                <div className="relative">
                                    <div className="w-16 h-16 border-4 border-white/5 rounded-full" />
                                    <div className="absolute top-0 left-0 w-16 h-16 border-4 border-t-violet-500 rounded-full animate-spin" />
                                </div>
                                <div className="text-center space-y-2">
                                    <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-white">
                                        Comparing Keywords...
                                    </h3>
                                    <p className="leading-7 text-zinc-500 text-sm italic">
                                        "Data beats opinions every time."
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Empty state feature cards */}
                        {!comparison && !loading && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="group bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all hover:shadow-lg hover:shadow-violet-500/10">
                                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                        <TargetIcon className="w-6 h-6 text-violet-400" />
                                    </div>
                                    <h3 className="scroll-m-20 text-lg font-semibold tracking-tight text-white mb-1.5">Data-Driven Decisions</h3>
                                    <p className="leading-7 text-zinc-400 text-xs">Stop guessing — compare keywords with real Google Trends data.</p>
                                </div>
                                <div className="group bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all hover:shadow-lg hover:shadow-blue-500/10">
                                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                        <CheckCircledIcon className="w-6 h-6 text-blue-400" />
                                    </div>
                                    <h3 className="scroll-m-20 text-lg font-semibold tracking-tight text-white mb-1.5">Choose Winners</h3>
                                    <p className="leading-7 text-zinc-400 text-xs">Identify the highest-interest keywords with rising momentum.</p>
                                </div>
                                <div className="group bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all hover:shadow-lg hover:shadow-emerald-500/10">
                                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-500/20 to-green-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                        <RocketIcon className="w-6 h-6 text-emerald-400" />
                                    </div>
                                    <h3 className="scroll-m-20 text-lg font-semibold tracking-tight text-white mb-1.5">12-Month View</h3>
                                    <p className="leading-7 text-zinc-400 text-xs">See full trend history to spot rising and declining patterns.</p>
                                </div>
                            </div>
                        )}

                        {/* Results */}
                        {comparison && !loading && (
                            <div className="space-y-6">

                                {/* Cache status bar */}
                                <div className="flex items-center justify-between px-1">
                                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                                        {isFresh ? (
                                            <span className="text-emerald-400">Live data</span>
                                        ) : (
                                            <>
                                                <span>Cached</span>
                                                {cachedAt && (
                                                    <span>· {Math.floor((Date.now() - new Date(cachedAt).getTime()) / (1000 * 60 * 60 * 24))}d ago</span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleCompare(true)}
                                        className="text-zinc-400 hover:text-white gap-1.5 text-xs h-7"
                                    >
                                        <ReloadIcon className="w-3 h-3" />
                                        Refresh
                                    </Button>
                                </div>

                                {/* Trend Chart */}
                                <Card className="bg-zinc-900/40 backdrop-blur-xl border-white/10">
                                    <CardHeader>
                                        <CardTitle className="text-lg text-white">Interest Over Time (Last 12 Months)</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        {comparison.chart_data?.labels?.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={280}>
                                                <LineChart data={buildChartData(comparison.chart_data)} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                                    <XAxis
                                                        dataKey="label"
                                                        tick={{ fill: '#71717a', fontSize: 11 }}
                                                        tickLine={false}
                                                        axisLine={false}
                                                        interval="preserveStartEnd"
                                                    />
                                                    <YAxis
                                                        domain={[0, 100]}
                                                        tick={{ fill: '#71717a', fontSize: 11 }}
                                                        tickLine={false}
                                                        axisLine={false}
                                                    />
                                                    <Tooltip content={<CustomTooltip />} />
                                                    <Legend
                                                        wrapperStyle={{ paddingTop: '16px', fontSize: '12px', color: '#a1a1aa' }}
                                                    />
                                                    {comparison.chart_data.datasets.map((ds, i) => (
                                                        <Line
                                                            key={ds.label}
                                                            type="monotone"
                                                            dataKey={ds.label}
                                                            stroke={KEYWORD_COLORS[i % KEYWORD_COLORS.length].line}
                                                            strokeWidth={2}
                                                            dot={false}
                                                            activeDot={{ r: 4 }}
                                                        />
                                                    ))}
                                                </LineChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <p className="text-zinc-500 text-sm text-center py-8">No chart data available</p>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Per-keyword stats table */}
                                <Card className="bg-zinc-900/40 backdrop-blur-xl border-white/10">
                                    <CardHeader>
                                        <CardTitle className="text-lg text-white">Keyword Breakdown</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-3">
                                            {(comparison.keyword_stats || []).map((kw, idx) => {
                                                const TrendIcon = getTrendIcon(kw.trend_direction);
                                                const trendColor = getTrendColor(kw.trend_direction);
                                                const dsIndex = comparison.chart_data?.datasets?.findIndex(d => d.label === kw.keyword) ?? -1;
                                                const colorSet = KEYWORD_COLORS[(dsIndex >= 0 ? dsIndex : idx) % KEYWORD_COLORS.length];
                                                const isWinner = kw.keyword === comparison.winner?.keyword;

                                                return (
                                                    <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${isWinner ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-zinc-800/30'}`}>
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <span className="w-3 h-3 rounded-full shrink-0" style={{ background: colorSet.line }} />
                                                            <span className="text-white font-medium truncate">{kw.keyword}</span>
                                                            {isWinner && (
                                                                <Badge className="bg-violet-500/20 text-violet-300 border-violet-500/30 text-xs shrink-0">
                                                                    Winner
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-4 shrink-0 ml-3">
                                                            <div className="text-right hidden sm:block">
                                                                <div className="text-xs text-zinc-500">Avg Interest</div>
                                                                <div className="text-sm font-medium text-white">{kw.avg_interest}/100</div>
                                                            </div>
                                                            <div className="text-right hidden sm:block">
                                                                <div className="text-xs text-zinc-500">Peak</div>
                                                                <div className="text-sm font-medium text-white">{kw.peak_interest}/100</div>
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                <TrendIcon className={`w-4 h-4 ${trendColor}`} />
                                                                <span className={`text-sm font-medium ${trendColor}`}>
                                                                    {kw.trend_change > 0 ? '+' : ''}{kw.trend_change}%
                                                                </span>
                                                            </div>
                                                            <Button
                                                                size="sm"
                                                                className="bg-violet-600 hover:bg-violet-700 text-white text-xs h-7 px-3"
                                                                onClick={() => { setPrefilledTopic(kw.keyword); setShowQuickPost(true); }}
                                                            >
                                                                <PlusIcon className="w-3 h-3 mr-1" />
                                                                Generate
                                                            </Button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Recommendation */}
                                <Card className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border-violet-500/20">
                                    <CardHeader>
                                        <CardTitle className="text-lg text-white flex items-center gap-2">
                                            💡 Recommendation
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="p-4 bg-white/5 rounded-lg">
                                            <p className="text-white font-medium">
                                                Use <span className="text-violet-300">"{comparison.winner?.keyword}"</span> — {comparison.winner?.reason}
                                            </p>
                                        </div>
                                        <div className="flex gap-3 pt-2">
                                            <Button
                                                className="flex-1 bg-violet-600 hover:bg-violet-700 text-white gap-2"
                                                onClick={() => { setPrefilledTopic(comparison.winner?.keyword || ''); setShowQuickPost(true); }}
                                            >
                                                <PlusIcon className="w-4 h-4" />
                                                Generate Article for "{comparison.winner?.keyword}"
                                            </Button>
                                            <Button
                                                variant="outline"
                                                className="gap-2"
                                                onClick={() => setComparison(null)}
                                            >
                                                <MagnifyingGlassIcon className="w-4 h-4" />
                                                Compare More
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>

                            </div>
                        )}
                    </div>
                </main>

                {/* QuickPost Modal */}
                {showQuickPost && (
                    <QuickPost
                        user={user}
                        onClose={(destination) => {
                            setShowQuickPost(false);
                            setPrefilledTopic('');
                            if (destination === 'topics') navigate('/topic-queue');
                        }}
                        prefilledTopic={prefilledTopic}
                        prefilledLocation={locationCode}
                    />
                )}
        </div>
    );
}

export default KeywordComparison;
